import { Suspense, lazy } from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { RootLayout } from '@/layouts/RootLayout';
import { DashboardLayout } from '@/layouts/DashboardLayout';
import { MemberLayout } from '@/layouts/MemberLayout';
import { PillarChairLayout } from '@/layouts/PillarChairLayout';
import { ProjectManagerLayout } from '@/layouts/ProjectManagerLayout/ProjectManagerLayout';
import { TaskForceChairLayout } from '@/layouts/TaskForceChairLayout';
import { ExecutiveLayout } from '@/layouts/ExecutiveLayout';
import { AdminLayout } from '@/layouts/AdminLayout';
import { AuthGuard } from './guards/AuthGuard';
import { GuestGuard } from './guards/GuestGuard';
import { RoleGuard } from './guards/RoleGuard';
import { PermissionGuard } from './guards/PermissionGuard';
import { PermissionRedirectGuard } from './guards/PermissionRedirectGuard';
import { ROUTES, ROLES, PERMISSIONS, ADMIN_PORTAL_PERMISSIONS } from '@/constants';
import {
    publicRoutes,
    dashboardRoutes,
    memberRoutes,
    pillarChairRoutes,
    projectManagerRoutes,
    taskForceChairRoutes,
    executiveRoutes,
    adminRoutes,
    errorRoutes,
} from './routes';

const ForcePasswordChangePage = lazy(() => import('@/pages/auth/ForcePasswordChangePage'));

function PageLoader() {
    return (
        <div className="flex h-full min-h-[50vh] items-center justify-center">
            <div className="flex flex-col items-center gap-3">
                <div className="h-8 w-8 animate-spin rounded-full border-[3px] border-primary-700 border-t-transparent" />
                <p className="text-sm text-muted-foreground">Loading…</p>
            </div>
        </div>
    );
}

function lazyWrap(Component) {
    return (
        <Suspense fallback={<PageLoader />}>
            <Component />
        </Suspense>
    );
}

const router = createBrowserRouter([
    {
        element: <RootLayout />,
        children: [
            // Landing page (guest only)
            ...publicRoutes.map((route) => ({
                path: route.path,
                element: (
                    <GuestGuard>
                        {lazyWrap(route.element)}
                    </GuestGuard>
                ),
            })),

            // Redirect /login to /
            {
                path: ROUTES.LOGIN,
                element: <Navigate to={ROUTES.HOME} replace />,
            },

            // Forced password change (authenticated, no role/permission gates)
            {
                path: ROUTES.CHANGE_PASSWORD,
                element: <AuthGuard>{lazyWrap(ForcePasswordChangePage)}</AuthGuard>,
            },

            // Dashboard routes (authenticated)
            {
                element: (
                    <AuthGuard>
                        <PermissionGuard requiredPermissions={[PERMISSIONS.VIEW_TASK]}>
                            <DashboardLayout />
                        </PermissionGuard>
                    </AuthGuard>
                ),
                children: dashboardRoutes.map((route) => ({
                    path: route.path,
                    element: lazyWrap(route.element),
                })),
            },

            // Member routes (authenticated)
            {
                path: ROUTES.MEMBER,
                element: (
                    <AuthGuard>
                        <PermissionGuard requiredPermissions={[PERMISSIONS.VIEW_OWN_TASK]}>
                            <MemberLayout />
                        </PermissionGuard>
                    </AuthGuard>
                ),
                children: memberRoutes.map((route) => ({
                    ...(route.index ? { index: true } : { path: route.path }),
                    element: lazyWrap(route.element),
                })),
            },

            // Pillar-chair routes (authenticated + role)
            {
                path: ROUTES.PILLAR_CHAIR,
                element: (
                    <AuthGuard>
                        <RoleGuard requiredRole={ROLES.PILLAR_CHAIR} redirectTo={ROUTES.HOME}>
                            <PermissionGuard requiredPermissions={[PERMISSIONS.VIEW_PILLAR]}>
                                <PillarChairLayout />
                            </PermissionGuard>
                        </RoleGuard>
                    </AuthGuard>
                ),
                children: pillarChairRoutes.map((route) => ({
                    ...(route.index ? { index: true } : { path: route.path }),
                    element: lazyWrap(route.element),
                })),
            },

            // Project Manager portal
            {
                path: ROUTES.PROJECT_MANAGER,
                element: (
                    <AuthGuard>
                        <RoleGuard requiredRole={ROLES.PROJECT_MANAGER} redirectTo={ROUTES.HOME}>
                            <PermissionGuard requiredPermissions={[PERMISSIONS.VIEW_PROJECT]}>
                                <ProjectManagerLayout />
                            </PermissionGuard>
                        </RoleGuard>
                    </AuthGuard>
                ),
                children: projectManagerRoutes.map((route) => ({
                    ...(route.index ? { index: true } : { path: route.path }),
                    element: lazyWrap(route.element),
                })),
            },

            // Task-force-chair routes (authenticated + role)
            {
                path: ROUTES.TASK_FORCE_CHAIR,
                element: (
                    <AuthGuard>
                        <RoleGuard requiredRole={ROLES.TASK_FORCE_CHAIR} redirectTo={ROUTES.HOME}>
                            <PermissionGuard requiredPermissions={[PERMISSIONS.VIEW_TASK_FORCE]}>
                                <TaskForceChairLayout />
                            </PermissionGuard>
                        </RoleGuard>
                    </AuthGuard>
                ),
                children: taskForceChairRoutes.map((route) => ({
                    ...(route.index ? { index: true } : { path: route.path }),
                    element: lazyWrap(route.element),
                })),
            },

            // Executive routes (authenticated + role)
            {
                path: ROUTES.EXECUTIVE,
                element: (
                    <AuthGuard>
                        <RoleGuard requiredRole={ROLES.EXECUTIVE} redirectTo={ROUTES.HOME}>
                            <PermissionGuard requiredPermissions={[PERMISSIONS.VIEW_REPORT]}>
                                <ExecutiveLayout />
                            </PermissionGuard>
                        </RoleGuard>
                    </AuthGuard>
                ),
                children: executiveRoutes.map((route) => ({
                    ...(route.index ? { index: true } : { path: route.path }),
                    element: lazyWrap(route.element),
                })),
            },

            // Admin routes (authenticated + MANAGE_USER)
            {
                path: ROUTES.ADMIN,
                element: (
                    <AuthGuard>
                        <PermissionRedirectGuard requiredPermissions={ADMIN_PORTAL_PERMISSIONS}>
                            <PermissionGuard requiredPermissions={[PERMISSIONS.VIEW_TASK_FORCE]}>
                                <AdminLayout />
                            </PermissionGuard>
                        </PermissionRedirectGuard>
                    </AuthGuard>
                ),
                children: adminRoutes.map((route) => ({
                    ...(route.index ? { index: true } : { path: route.path }),
                    element: lazyWrap(route.element),
                })),
            },

            // Error / catch-all
            ...errorRoutes.map((route) => ({
                path: route.path,
                element: lazyWrap(route.element),
            })),
        ],
    },
]);

export function AppRouter() {
    return <RouterProvider router={router} />;
}

export default AppRouter;
